import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, Camera, ArrowLeft, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDT, useData } from "@/ops/ui";

const GPS_BAD = ["outside", "no_location"];

const getPos = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition((p) => resolve(p.coords), () => resolve(null), { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
});

// stopping an already-stopped scanner is expected and harmless
const logStop = () => {};

function stopReader(qr) {
  // stop() throws when the scanner never started (camera denied / StrictMode remount)
  if (qr.isScanning) qr.stop().catch(logStop);
}

function Reader({ onCode }) {
  const [err, setErr] = useState("");
  useEffect(() => {
    const qr = new Html5Qrcode("qr-reader");
    let done = false;
    qr.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, (text) => {
      if (done) return;
      done = true;
      qr.stop().catch(logStop).finally(() => onCode(text));
    }).catch(() => setErr("Kamera açılamadı. Tarayıcıya kamera izni verin veya telefonunuzun kamerasıyla QR kodu okutun."));
    return () => {
      if (!done) stopReader(qr);
    };
  }, [onCode]);
  return (
    <div>
      <div id="qr-reader" className="overflow-hidden rounded-lg bg-black" data-testid="qr-reader" />
      {err && <p className="mt-3 rounded bg-orange-50 p-3 text-sm text-orange-800" data-testid="qr-camera-error">{err}</p>}
    </div>
  );
}

function resultView(res) {
  const gpsBad = GPS_BAD.includes(res.scan?.location_status);
  if (!res.ok) return { tone: "bg-red-50", icon: "text-red-600", title: "Okutma kaydedilmedi", gpsBad };
  if (gpsBad) return { tone: "bg-orange-50", icon: "text-orange-600", title: "Kaydedildi – GPS doğrulanamadı", gpsBad };
  const flagged = (res.scan?.flags || []).length > 0;
  return { tone: flagged ? "bg-orange-50" : "bg-emerald-50", title: "Okutma kaydedildi", success: true, gpsBad };
}

function Result({ res, again }) {
  const v = resultView(res);
  return (
    <div className={`rounded-lg p-5 ${v.tone}`} data-testid="scan-result">
      <p className="flex items-center gap-2 text-lg font-bold">
        {v.success ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <AlertTriangle className={`h-6 w-6 ${v.icon}`} />}{v.title}
      </p>
      {res.ok && <p className="mt-1 text-sm">{res.scan.checkpoint_name} · {fmtDT(res.scan.at)} (sunucu saati)</p>}
      {!res.ok && <p className="mt-1 text-sm" data-testid="scan-error">{res.error}</p>}
      {(res.scan?.flags || []).map((f) => <p key={f} className="mt-1 text-sm text-orange-800">• {f}</p>)}
      {v.gpsBad && <p className="mt-2 text-sm font-semibold text-red-700" data-testid="scan-not-counted">Bu okutma GPS doğrulamasını geçemediği için devriye tamamlanmış sayılmaz. Kontrol noktasına yaklaşıp 5 dakika sonra tekrar okutun.</p>}
      <button onClick={again} className="btn-red mt-4 w-full justify-center !py-3" data-testid="scan-again-btn">Yeni Okutma</button>
    </div>
  );
}

function Busy() {
  return <p className="flex items-center gap-2 rounded-lg bg-white p-5" data-testid="scan-busy"><Loader2 className="h-5 w-5 animate-spin text-[color:var(--red)]" /> Konum alınıyor ve kaydediliyor...</p>;
}

function PhotoStep({ photo, setPhoto, submit }) {
  return (
    <div className="rounded-lg bg-white p-5" data-testid="scan-photo-step">
      <p className="mb-3 text-sm">Bu projede okutma için <b>fotoğraf zorunludur</b>.</p>
      <label className="btn-outline w-full justify-center !py-3"><Camera className="h-5 w-5" /> {photo ? "Fotoğrafı değiştir" : "Fotoğraf çek"}
        <input type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="scan-photo-input" />
      </label>
      {photo && <p className="mt-2 text-xs text-zinc-500">{photo.name}</p>}
      <button disabled={!photo} onClick={submit} className="btn-red mt-4 w-full justify-center !py-3" data-testid="scan-submit-btn">Okutmayı Kaydet</button>
    </div>
  );
}

const checkpointId = (code) => code.split("q=").pop().split(".")[0];

function photoNeeded(projects, cid) {
  const project = projects?.find((p) => p.checkpoints.some((c) => c.id === cid));
  if (!project) return false;
  return !!project.photo_required || !!project.checkpoints.find((c) => c.id === cid)?.photo;
}

async function postScan(code, nonce, photo) {
  const pos = await getPos();
  const fd = new FormData();
  fd.append("code", code);
  fd.append("nonce", nonce);
  if (pos) { fd.append("lat", pos.latitude); fd.append("lng", pos.longitude); fd.append("accuracy", pos.accuracy); }
  if (photo) fd.append("photo", photo);
  try {
    return (await api.post("/ops/field/scan", fd)).data;
  } catch (e) {
    return { ok: false, error: errMsg(e) };
  }
}

function useScan(initialCode, projects) {
  const [code, setCode] = useState(initialCode);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const nonce = useRef(crypto.randomUUID());
  const needPhoto = photoNeeded(projects, checkpointId(code));
  const submit = useCallback(async () => {
    setBusy(true);
    setRes(await postScan(code, nonce.current, photo));
    setBusy(false);
  }, [code, photo]);
  useEffect(() => {
    if (code && projects && !needPhoto && !res && !busy) submit();
  }, [code, projects, needPhoto, res, busy, submit]);
  const reset = () => { setCode(""); setPhoto(null); setRes(null); nonce.current = crypto.randomUUID(); };
  return { code, setCode, photo, setPhoto, busy, res, submit, reset };
}

function ScanStep({ s, ready, again }) {
  if (s.res) return <Result res={s.res} again={again} />;
  if (!s.code) return <Reader onCode={s.setCode} />;
  if (s.busy || !ready) return <Busy />;
  return <PhotoStep photo={s.photo} setPhoto={s.setPhoto} submit={s.submit} />;
}

export default function Scan() {
  const [params, setParams] = useSearchParams();
  const [d] = useData("/ops/field/projects");
  const s = useScan(params.get("q") ? `q=${params.get("q")}` : "", d?.projects);
  const again = () => { s.reset(); setParams({}); };
  if (d && d.projects === undefined) return null;
  return (
    <div className="mx-auto max-w-md">
      <Link to="/operasyon" className="mb-3 inline-flex items-center gap-1 text-sm text-zinc-500"><ArrowLeft className="h-4 w-4" /> Görevlerim</Link>
      <h1 className="mb-4 text-2xl font-extrabold" data-testid="ops-page-title">QR Kod Okut</h1>
      <ScanStep s={s} ready={!!d} again={again} />
      <p className="mt-4 text-xs text-zinc-500">Okutma zamanı sunucu tarafından kaydedilir. Konum izni verdiyseniz konumunuz yalnızca bu okutma için kaydedilir; izin vermezseniz yönetici ayarına göre okutma reddedilir veya "konum alınamadı" olarak uyarılı kaydedilir ve devriye tamamlanmış sayılmaz.</p>
    </div>
  );
}
